/**
 * Stytch Consumer SDK client + env validation.
 *
 * Single source of truth for the Stytch instance used across the
 * editor. Reads VITE_STYTCH_* env vars and fails fast in dev if any
 * are missing — better to crash on mount than to silently 401 later.
 *
 * The SDK reads tokens out of cookies it manages itself
 * (stytch_session, stytch_session_jwt). We never store tokens
 * manually; the SDK + Stytch's domain handle that.
 *
 * Auth flows in use are all Consumer SDK methods: magic links
 * (stytch.magicLinks.*), social OAuth (stytch.oauth.google/github.*),
 * and passwords (stytch.passwords.*) — all completing at /auth/callback.
 *
 * NOTE: we do NOT use Stytch Connected Apps (the OAuth/OIDC IdP feature).
 * A VITE_STYTCH_CONNECTED_APP_CLIENT_ID was previously required here but
 * nothing ever consumed it, so it was dropped (2026-06-26). If we later
 * make Lilia an OAuth identity provider (e.g. for an MCP server or
 * third-party integrations), wire the Connected App flow then and set the
 * Connected App's Authorization URL in the Stytch dashboard.
 */
import { createStytchClient } from "@stytch/react";

interface StytchEnv {
  publicToken: string;
  projectId: string;
  domain: string;
}

function readEnv(): StytchEnv {
  const publicToken = import.meta.env.VITE_STYTCH_PUBLIC_TOKEN;
  const projectId = import.meta.env.VITE_STYTCH_PROJECT_ID;
  const domain = import.meta.env.VITE_STYTCH_DOMAIN;

  // Allow E2E tests to bypass Stytch entirely with VITE_E2E.
  const isE2E = import.meta.env.VITE_E2E === "true";
  if (isE2E) {
    return {
      publicToken: publicToken ?? "public-token-test-e2e-placeholder",
      projectId: projectId ?? "project-test-e2e-placeholder",
      domain: domain ?? "https://e2e.stytch.dev",
    };
  }

  const missing: string[] = [];
  if (!publicToken) missing.push("VITE_STYTCH_PUBLIC_TOKEN");
  if (!projectId) missing.push("VITE_STYTCH_PROJECT_ID");
  if (!domain) missing.push("VITE_STYTCH_DOMAIN");

  if (missing.length > 0) {
    throw new Error(
      `Stytch env vars missing: ${missing.join(", ")}. ` +
        "Add them to .env.local. See lilia-docs/launch-readiness/" +
        "2026-05-16-auth-pages-custom-design.md for the values.",
    );
  }

  return { publicToken, projectId, domain };
}

export const stytchEnv = readEnv();

/**
 * Single Stytch client instance shared across all auth routes.
 * createStytchClient returns the headless-shaped client; React hooks
 * (useStytch, useStytchUser, useStytchSession) work against this
 * instance once it's passed to StytchProvider in main.tsx.
 */
export const stytchClient = createStytchClient(stytchEnv.publicToken);

/**
 * Build the redirect URL passed to social / password reset / email
 * verification flows. Same callback handles all three; the SDK
 * encodes the flow type in the URL.
 */
export function authCallbackUrl(): string {
  return `${window.location.origin}/auth/callback`;
}

/**
 * Where we send users after Stytch returns success on a flow that
 * doesn't have its own success page (e.g. social OAuth login).
 */
export function postSignInRedirectUrl(returnTo?: string | null): string {
  if (returnTo && returnTo.startsWith("/")) {
    return `${window.location.origin}${returnTo}`;
  }
  return `${window.location.origin}/`;
}
